import { useEffect, useState } from 'react';
import { LoaderCircle, PanelRightClose, PanelRightOpen } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export type ActionBarAction = {
    icon?: React.ReactNode;
    label: string;
    onClick: () => void;
    /** Retire complètement l'action (et la section si elle devient vide). */
    hidden?: boolean;
    /** Garde l'action visible mais inactive. */
    disabled?: boolean;
    loading?: boolean;
    variant?: 'default' | 'destructive';
    dataTest?: string;
};

export type ActionBarSection = {
    title?: string;
    actions: ActionBarAction[];
};

type ActionBarProps = {
    actionSections: ActionBarSection[];
    isInitiallyOpen?: boolean;
    children?: React.ReactNode;
};

export const ActionBar = ({ actionSections, isInitiallyOpen = false, children }: ActionBarProps) => {
    const [isOpen, setIsOpen] = useState(isInitiallyOpen);

    // La barre suit l'intention du parent : elle s'ouvre/se ferme avec la sélection.
    useEffect(() => {
        setIsOpen(isInitiallyOpen);
    }, [isInitiallyOpen]);

    const sections = actionSections
        .map((section) => ({ ...section, actions: section.actions.filter((action) => !action.hidden) }))
        .filter((section) => section.actions.length > 0);

    return (
        <TooltipProvider delayDuration={0}>
            <aside
                data-slot="action-bar"
                data-state={isOpen ? 'expanded' : 'collapsed'}
                className={cn(
                    'ml-auto flex h-full shrink-0 flex-col border-l border-border bg-card',
                    'transition-[width] duration-300 ease-in-out',
                    isOpen ? 'w-60' : 'w-12',
                )}
            >
                <div className="flex h-12 shrink-0 items-center px-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-primary"
                        onClick={() => setIsOpen((open) => !open)}
                        data-test="expand-icon"
                        aria-expanded={isOpen}
                        aria-label={isOpen ? "Réduire la barre d'actions" : "Déployer la barre d'actions"}
                    >
                        {isOpen ? <PanelRightClose /> : <PanelRightOpen />}
                    </Button>
                </div>

                <ScrollArea className="flex-1">
                    <div className="flex flex-col gap-3 px-2 pb-3">
                        {sections.map((section, index) => (
                            <div key={index} className="flex flex-col gap-0.5">
                                {index > 0 ? <Separator className="my-1" /> : null}
                                {isOpen && section.title ? (
                                    <span className="px-2 pt-1 pb-0.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                                        {section.title}
                                    </span>
                                ) : null}
                                {section.actions.map((action, actionIndex) => (
                                    <ActionItem key={actionIndex} action={action} isOpen={isOpen} />
                                ))}
                            </div>
                        ))}
                        {isOpen && children ? <div className="px-1 pt-1">{children}</div> : null}
                    </div>
                </ScrollArea>
            </aside>
        </TooltipProvider>
    );
};

const ActionItem = ({ action, isOpen }: { action: ActionBarAction; isOpen: boolean }) => {
    const { icon, label, onClick, loading, disabled, variant = 'default', dataTest } = action;

    const button = (
        <Button
            type="button"
            variant={variant === 'destructive' ? 'destructive' : 'ghost'}
            size={isOpen ? 'sm' : 'icon'}
            className={cn(isOpen && 'w-full justify-start')}
            onClick={onClick}
            disabled={disabled || loading}
            data-test={dataTest ?? 'action-bar-action'}
        >
            {loading ? <LoaderCircle className="animate-spin" /> : icon}
            {isOpen ? <span className="truncate">{label}</span> : null}
        </Button>
    );

    if (isOpen) {
        return button;
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{button}</TooltipTrigger>
            <TooltipContent side="left">{label}</TooltipContent>
        </Tooltip>
    );
};
